// A tappable (i) that opens its note in a popover and closes on a second tap, a tap
// elsewhere, Escape, a scroll or focus moving on. Used where a hover title would leave
// touch screens nothing. Given a `trigger`, that text stands in for the (i) and also
// opens the note on hover, staying open while the pointer is over the note itself so a
// link in it can be clicked. Its text takes review comments as part of the row or card
// it opened from, and it stays open while it holds the selection being commented on.
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import type { FocusEvent, ReactNode } from "react";
import { createPortal } from "react-dom";

import { inheritReviewAnchor, REVIEW_CHROME, useReview } from "../utils/reviewState";
import { useAnchoredPlacement } from "../utils/useAnchoredPlacement";
import { useClickOutside } from "../utils/useClickOutside";
import styles from "./InfoToggle.module.css";

const MARGIN = 8;
// Time to cross from the trigger to the note before it closes.
const HOVER_CLOSE_MS = 150;

export const InfoToggle = ({
  label,
  children,
  trigger,
}: {
  label: string;
  children: ReactNode;
  trigger?: ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLSpanElement>(null);
  const id = useId();
  const close = useCallback(() => setOpen(false), []);
  const ref = useClickOutside<HTMLSpanElement>(open, close, [panel], REVIEW_CHROME);
  const closing = useRef<ReturnType<typeof setTimeout> | null>(null);
  const keepOpen = useCallback(() => {
    if (closing.current) clearTimeout(closing.current);
    closing.current = null;
  }, []);
  const hoverOpen = useCallback(() => {
    keepOpen();
    setOpen(true);
  }, [keepOpen]);
  const hoverClose = useCallback(() => {
    keepOpen();
    // Text selected in it is about to be commented on: the pointer heads for the button.
    const selection = window.getSelection();
    if (selection && !selection.isCollapsed && panel.current?.contains(selection.anchorNode)) {
      return;
    }
    closing.current = setTimeout(close, HOVER_CLOSE_MS);
  }, [keepOpen, close]);
  useEffect(() => keepOpen, [keepOpen]);
  const hover = trigger !== undefined;

  // In the body, fixed to the (i): a table cell is too narrow to read it in, and the
  // table's scroll box would clip it. Review anchoring is set before it is placed.
  const portalId = useReview()?.portalId;
  useLayoutEffect(() => {
    if (open && portalId && panel.current && button.current) {
      inheritReviewAnchor(button.current, panel.current, portalId);
    }
  }, [open, portalId]);
  const { place, position } = useAnchoredPlacement(open, button, panel, MARGIN);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    // Any scroll but its own: the (i) may have scrolled out of the table's box.
    const onScroll = (e: Event) => {
      if (!panel.current?.contains(e.target as Node)) close();
    };
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", position);
    return () => {
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", position);
    };
  }, [open, close, position]);

  // Focus moving to another control (Tab to the next (i)) closes it, so two never stack.
  const onBlur = (e: FocusEvent) => {
    const next = e.relatedTarget as Node | null;
    if (
      next &&
      !button.current?.contains(next) &&
      !panel.current?.contains(next) &&
      !(next instanceof Element && next.closest(REVIEW_CHROME))
    ) {
      close();
    }
  };

  return (
    <span ref={ref} className={styles.wrap}>
      <button
        ref={button}
        type="button"
        className={hover ? styles.trigger : styles.button}
        aria-label={hover ? undefined : label}
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        aria-describedby={open ? id : undefined}
        // A tap on a touch screen also fires mouseenter, so the text trigger only opens.
        onClick={hover ? hoverOpen : () => setOpen((was) => !was)}
        onMouseEnter={hover ? hoverOpen : undefined}
        onMouseLeave={hover ? hoverClose : undefined}
        onBlur={onBlur}
      >
        {hover ? trigger : "i"}
      </button>
      {open &&
        createPortal(
          <span
            ref={panel}
            id={id}
            role="note"
            tabIndex={-1}
            className={styles.panel}
            style={place}
            onBlur={onBlur}
            onMouseEnter={hover ? keepOpen : undefined}
            onMouseLeave={hover ? hoverClose : undefined}
          >
            {children}
          </span>,
          document.body,
        )}
    </span>
  );
};
